import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { MotDePasseOublie } from "@/components/MotDePasseOublie";
import { VerificationAppareil } from "@/components/VerificationAppareil";

export default function Login() {
  const { signIn, etat, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oubli, setOubli] = useState(false);
  const [info, setInfo] = useState("");
  const [defi, setDefi] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const verif = await signIn("login", form);
      if (verif) { setDefi(verif); return; }
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  if (etat?.local && !etat.compte) return <Navigate to="/bienvenue" replace />;
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] grid-paper" />
        <p className="relative text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400">Rapprochement bancaire · Québec</p>
        <div className="relative">
          <h1 className="text-5xl font-bold tracking-tight text-white leading-[1.05]">
            Chaque versement
            <br />
            Airbnb et Booking,
            <br />
            <span className="text-emerald-400">retrouvé à la cenne.</span>
          </h1>
          <p className="mt-6 max-w-md text-slate-400 text-sm leading-relaxed">
            Importez votre relevé Desjardins et vos rapports de plateformes. L'application associe les dépôts, détecte les
            virements groupés et vérifie la TPS, la TVQ et la taxe d'hébergement de 3,5 %.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-6 font-mono text-xs text-slate-500">
          <div><p className="text-2xl text-white">5 %</p>TPS</div>
          <div><p className="text-2xl text-white">9,975 %</p>TVQ</div>
          <div><p className="text-2xl text-white">3,5 %</p>Hébergement</div>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-10 bg-slate-100">
        <div className="relative w-full max-w-[340px] rounded-[2.75rem] border-[10px] border-slate-900 bg-white shadow-2xl shadow-slate-900/30 rise" data-testid="login-phone-frame">
          <div className="absolute left-1/2 top-2 h-6 w-28 -translate-x-1/2 rounded-full bg-slate-900" aria-hidden="true" />
          <div className="flex justify-between px-7 pt-3 text-[10px] font-mono text-slate-500" aria-hidden="true"><span>9:41</span><span>5G ▮▮▮</span></div>
        {defi ? <VerificationAppareil defi={defi} onSucces={() => navigate("/")} onRetour={(m) => { setDefi(null); setForm({ ...form, password: "" }); setError(m || ""); setInfo(""); }} /> : oubli ? <MotDePasseOublie emailInitial={form.email} onRetour={(m) => { setOubli(false); setInfo(m); setError(""); }} /> : (
        <form onSubmit={submit} className="px-7 pt-8 pb-6" data-testid="login-form">
          <ShieldCheck className="h-7 w-7 text-emerald-700" />
          <h2 className="mt-4 text-2xl font-bold tracking-tight">Connexion</h2>
          <p className="mt-1 text-sm text-slate-500">Vos données financières restent privées.</p>
          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput id="password" required autoComplete="current-password" value={form.password} onChange={set("password")} testId="login-password-input" />
              {etat?.local
                ? <p className="text-xs text-slate-500" data-testid="login-oubli-local">Mot de passe oublié : voir « Mot de passe oublié » dans le guide d'installation.</p>
                : <button type="button" onClick={() => { setOubli(true); setInfo(""); }} className="text-xs text-emerald-700 hover:underline" data-testid="login-forgot-link">Mot de passe oublié ?</button>}
            </div>
            {info && <p className="text-sm text-emerald-700" data-testid="login-info">{info}</p>}
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Se connecter
            </Button>
          </div>
          <p className="mt-6 text-xs text-slate-500" data-testid="login-private-note">{etat?.local ? "Vos données restent uniquement sur cet ordinateur." : "Accès réservé au propriétaire de l'application."}</p>
        </form>
        )}
          <div className="mx-auto mb-2 h-1 w-28 rounded-full bg-slate-900" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
